import { ReactNode, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { useDownloadFile } from '@anytag/shared-hooks';
import { MIME_ACCEPT } from '@anytag/shared-constants';
import { DownloadFileProps } from '@anytag/shared-utils';
import { FileDrop, FileDropProps } from '@anytag/shared-molecules';
import { Anchor, BaseModal, BaseModalProps } from '@anytag/shared-atoms';

export interface UploadCsvModal
  extends Pick<BaseModalProps, 'open' | 'onClose'>,
    Omit<FileDropProps, 'title' | 'generateSignedUrl'> {
  title?: ReactNode;
  uploading?: boolean;
  onFileUploaded?: () => void;
  downloadTranslationKey?: string;
  downloadFile?: DownloadFileProps;
  uploadFile?: { url: URL; fileNamePrefix: string };
  onCustomUpload?: (acceptedFile: File) => void | Promise<void>;
}

export const UploadCsvModal = ({
  open,
  title,
  onClose,
  uploading,
  uploadFile,
  notes = [],
  downloadFile,
  onCustomUpload,
  downloadTranslationKey = 'User can download CSV template and also upload it',
  ...restProps
}: UploadCsvModal) => {
  const { t } = useTranslation();
  const childRef = useRef<{ newFile: File; requestUpload: () => void }>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { downloading, handleDownloadFile } = useDownloadFile();

  return (
    <BaseModal
      size="lg"
      open={open}
      onClose={onClose}
      title={title || t('Upload CSV')}
      footer={{
        cancelButton: { onClick: onClose },
        submitButton: {
          title: t('Button.Upload'),
          disabled: !uploadedFiles.length,
          loading: downloading || uploading,
          onClick: async () => {
            if (childRef.current) {
              if (onCustomUpload && childRef.current?.newFile) {
                await onCustomUpload(childRef.current.newFile);
              } else {
                childRef.current.requestUpload();
              }
            }
            onClose?.();
          }
        }
      }}
    >
      <FileDrop
        notes={notes}
        ref={childRef}
        multiple={false}
        accept={{ ...MIME_ACCEPT.CSV }}
        onDropCallback={setUploadedFiles}
        uploadURL={uploadFile?.url?.toString()}
        uploadFileName={uploadFile?.fileNamePrefix}
        description={
          downloadTranslationKey && downloadFile ? (
            <Trans
              i18nKey={downloadTranslationKey}
              components={{
                a: (
                  <Anchor
                    variant="blue"
                    css={{ display: 'inline' }}
                    onClick={(e) => {
                      e.preventDefault();

                      if (downloadFile) {
                        handleDownloadFile(downloadFile);
                      }
                    }}
                  />
                )
              }}
            />
          ) : undefined
        }
        {...restProps}
      />
    </BaseModal>
  );
};
